import React, { useState, useContext, useCallback, useEffect, useRef } from 'react'
import { Autocomplete, IconButton, Paper, Typography } from '@mui/material'
import { FieldConfig } from 'formik'
import FormikContext from '../../../context/formikContext'
import { t } from 'i18next'
import { Option } from '../../../types'
import { getNestedObject } from '../../../utils'
import OutlinedField from '../../common/fields/outlinedField'
import { ArrowDropDown } from '@mui/icons-material'

interface BaseAutoCompleteProps {
  name: string
  placeholder: string
  listItems: Option[]
  onChange: (event: any, newValue: any) => void
  onSearchChange?: (searchValue: string) => void
  minSearchLength?: number
  maxSearchLength?: number
  isPostalCode?: boolean
  preserveTypedValue?: boolean
  autoInitializeValue?: string
}

type Props = FieldConfig &
  Omit<React.ComponentProps<typeof Autocomplete>, 'renderInput' | 'options' | 'onChange'> &
  BaseAutoCompleteProps

const BaseAutoComplete = ({ 
  name,
  placeholder,
  listItems,
  onChange,
  onSearchChange,
  minSearchLength = 2,
  maxSearchLength = 50,
  isPostalCode = false,
  preserveTypedValue = false,
  autoInitializeValue,
  ...rest 
}: Props) => {
  const formik = useContext(FormikContext)
  const currentFormikValue = getNestedObject(name, formik.values)
  
  const [inputValue, setInputValue] = useState(() => {
    if (currentFormikValue) {
      if (isPostalCode) {
        return currentFormikValue
      }
      const selectedOption = listItems.find(item => item.id === currentFormikValue)
      return selectedOption?.name || ''
    }
    if (autoInitializeValue) {
      return autoInitializeValue
    }
    return ''
  })
  
  const isAutoInitializingRef = useRef(Boolean(autoInitializeValue))

  const [customError, setCustomError] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  
  // On mount: if we need to auto-initialize, trigger the search with the seed value
  useEffect(() => {
    if (isAutoInitializingRef.current && autoInitializeValue && onSearchChange) {
      onSearchChange(autoInitializeValue)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // When results arrive during auto-initialization, select the first match automatically
  useEffect(() => {
    if (!isAutoInitializingRef.current) return
    if (!listItems || listItems.length === 0) return
    console.log('auto initializing with list items:', listItems)
    const match = listItems.find(
      (item) => item.name.toLowerCase() === (autoInitializeValue || '').toLowerCase()
    ) || listItems[0]

    isAutoInitializingRef.current = false
    onChange(null, match)
    setInputValue(match.name)
    setIsOpen(false) 
    console.log('Auto-initialization effect ran with list items:', listItems)
  }, [listItems]) // eslint-disable-line react-hooks/exhaustive-deps

  const hasFormikError = Boolean(
    getNestedObject(name, formik.touched) && getNestedObject(name, formik.errors),
  )
  
  const shouldShowValidationError = hasFormikError && formik.submitCount > 0
  
  const displayError = shouldShowValidationError || customError
  const errorMessage = customError || (hasFormikError ? getNestedObject(name, formik.errors) : '')

  const normalizeString = useCallback((str: string) => {
    return str.replace(/\s+/g, '').toLowerCase()
  }, [])

  const validateOnBlur = useCallback(async () => {
    if (inputValue.length === 0) {
      const cityValue = formik.values.city || ''
      const postalCodeValue = formik.values.postalCode || ''
      
      if (!cityValue && !postalCodeValue) {
        setCustomError('Obligatoire')
        formik.setFieldError(name, '')
        return
      }
      setCustomError('')
      formik.setFieldError(name, '')
      return
    }

    if (isPostalCode) {
      const normalized = normalizeString(inputValue)
      if (normalized.length !== 6) {
        setCustomError('Le code postal doit contenir 6 caractères')
        formik.setFieldError(name, '')
        return
      }
    }
    
    setCustomError('')
    formik.setFieldError(name, '')
  }, [inputValue, isPostalCode, normalizeString, onSearchChange, listItems, formik.values.city, formik.values.postalCode, name, formik])

  const handleInputChange = useCallback((event: any, newInputValue: string) => {
    const shouldPreserveValue = isPostalCode || preserveTypedValue
    if (shouldPreserveValue && newInputValue === '' && inputValue !== '') {
      if (event?.type === 'blur' || !event || event.type === undefined) {
        return
      }
    }
    
    setInputValue(newInputValue)

    const lengthToCheck = isPostalCode ? normalizeString(newInputValue).length : newInputValue.length
    
    if (lengthToCheck >= minSearchLength && lengthToCheck <= maxSearchLength) {
      setIsOpen(true)
    } else {
      setIsOpen(false)
    }
    
    if (customError) {
      setCustomError('')
    }
    
    if (hasFormikError) {
      formik.setFieldError(name, '')
    }
    
    if (onSearchChange && lengthToCheck >= minSearchLength && lengthToCheck <= maxSearchLength) {
      onSearchChange(newInputValue)
    }
  }, [onSearchChange, minSearchLength, maxSearchLength, isPostalCode, preserveTypedValue, inputValue, customError, name, hasFormikError, formik, normalizeString])

  const handleChange = useCallback((event: any, newValue: any) => {
    if (typeof newValue === 'object' && newValue !== null) {
      setInputValue(newValue.name)
      setIsOpen(false)
    } else if (typeof newValue === 'string') {
      setInputValue(newValue)
      setIsOpen(false)
    } else {
      const shouldPreserveValue = isPostalCode || preserveTypedValue
      if (!shouldPreserveValue) {
        setInputValue('')
      }
    }
    
    if (customError) {
      setCustomError('')
    }
    
    if (hasFormikError) {
      formik.setFieldError(name, '')
    }
    
    onChange(event, newValue)
  }, [onChange, isPostalCode, preserveTypedValue, customError, name, hasFormikError, formik])

  const handleBlur = useCallback(() => {
    formik.setFieldTouched(name, true)
    setIsOpen(false)
    validateOnBlur()
  }, [formik, name, validateOnBlur])

  const handleFocus = useCallback(() => {
    if (customError) {
      setCustomError('')
    }
    
    if (hasFormikError) {
      formik.setFieldError(name, '')
    }
    
    const lengthToCheck = isPostalCode ? normalizeString(inputValue).length : inputValue.length

    if (lengthToCheck >= minSearchLength && lengthToCheck <= maxSearchLength) {
      setIsOpen(true)
    }
  }, [customError, name, hasFormikError, formik, inputValue, minSearchLength, maxSearchLength, isPostalCode, normalizeString])

  return (
    <Autocomplete
      getOptionLabel={(option: any) => option?.name || ''}
      options={listItems || []}
      size='small'
      freeSolo
      noOptionsText={t('common.noOptions')}
      disableClearable
      filterOptions={(options) => options}
      value={inputValue}
      inputValue={inputValue}
      open={isOpen}
      onInputChange={handleInputChange}
      onChange={handleChange}
      loading={false}
      PaperComponent={(props) => {
        const lengthToCheck = isPostalCode ? normalizeString(inputValue).length : inputValue.length
        const hasValidInput = lengthToCheck >= minSearchLength && lengthToCheck <= maxSearchLength
        
        if ((listItems?.length ?? 0) === 0 && hasValidInput) {
          return (
            <Paper 
              {...props} 
              style={{ 
                marginTop: '8px',
                fontSize: '16px',
                border: '1px solid #DADCDD',
                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.25)',
                borderRadius: '4px',
                padding: '16px',
                fontFamily: 'Overpass',
                color: 'rgb(68, 77, 78)',
                backgroundColor: 'white',
                ...props.style 
              }}
            >
              {t('common.noOptions')}
            </Paper>
          )
        }
        return <Paper {...props} />
      }}
      isOptionEqualToValue={(option: any, value: any) => {
        if (!option || !value) return false
        return option.id === value.id
      }}
      slotProps={{
        paper: {
          sx: {
            marginTop: '8px',
            fontSize: '16px',
            border: '1px solid #DADCDD',
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.25) !important',
            borderRadius: '4px',
            '& .MuiAutocomplete-listbox': {
              padding: '16px',
            },
            '& li.MuiAutocomplete-option': {
              fontFamily: 'Overpass',
              backgroundColor: 'white!important',
              padding: '0',
            },
            '& li:hover': {
              color: '#292929',
              fontWeight: '700',
            },
            '& li + li': {
              marginTop: '8px',
            },
          },
        },
      }}
      sx={{
        '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
          py: '10px',
        },
        '& .MuiButtonBase-root.MuiIconButton-root': {
          pt: '0',
        },
        '& .MuiButtonBase-root.MuiIconButton-root:hover': {
          backgroundColor: 'white',
        },
        '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiAutocomplete-input': {
          py: '0',
        },
        '& .MuiOutlinedInput-input::placeholder': {
          color: 'rgb(68, 77, 78)',
          opacity: 1,
        },
      }}
      {...rest}
      renderInput={(params) => (
        <OutlinedField
          placeholder={placeholder}
          {...params}
          onBlur={handleBlur}
          onFocus={handleFocus}
          error={Boolean(displayError)}
          helperText={errorMessage as React.ReactNode}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <IconButton edge="end" size="small">
                <ArrowDropDown />
              </IconButton>
            ),
          }}
        />
      )}
    />
  )
}

export default BaseAutoComplete